import React, { useState, useEffect } from "react";
import {
    Card,
    Typography,
    Box,
    Drawer,
    Button,
    Stack,
    CircularProgress,
    IconButton,
    Snackbar,
    TextField,
    Paper
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import API from "../api/api";

export default function CouponSelector({
    subtotal,
    onCouponApplied,
    selectedCoupon,
    couponDiscount,
    vendorId // 👈 ACCEPTED PROP
}) {
    const [open, setOpen] = useState(false);
    const [applyingCoupon, setApplyingCoupon] = useState(null);
    const [coupons, setCoupons] = useState([]);
    const [totalCoupons, setTotalCoupons] = useState(0);
    const [manualCode, setManualCode] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error",
    });

    useEffect(() => {
        if (!open || !vendorId) return;

        // 👇 Move the function INSIDE the useEffect to satisfy ESLint
        const loadCoupons = async () => {
            try {
                const user = JSON.parse(localStorage.getItem("user"));

                const res = await API.get(
                    `/coupons/available?user_id=${user.id}&vendor_id=${vendorId}`
                );

                setCoupons(res.data.coupons || []);
                setTotalCoupons(
                    res.data.total_active_coupons || 0
                );
            } catch (err) {
                console.log(err);
            }
        };

        loadCoupons();
    }, [open, vendorId]);

    // 🛡️ Extremely robust error message parser
    const getCouponErrorMessage = (error) => {
        // Handle JS Errors thrown manually
        if (error instanceof Error && error.message) {
            if (error.message.toLowerCase().includes("invalid")) return "❌ Wrong coupon code. Please check and try again.";
        }

        const detail =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "";

        const lowerDetail = typeof detail === 'string' ? detail.toLowerCase() : "";

        if (lowerDetail.includes("invalid coupon")) return "❌ Wrong coupon code. Please check and try again.";
        if (lowerDetail.includes("not started")) return "⏳ This offer is not active yet.";
        if (lowerDetail.includes("expired")) return "⌛ This coupon has expired.";
        if (lowerDetail.includes("exhausted")) return "😔 This coupon has reached its usage limit.";
        if (lowerDetail.includes("first order")) return "🎉 This offer is only available for your first order.";
        if (lowerDetail.includes("already used")) return "⚠️ You've already used this coupon.";
        if (lowerDetail.includes("minimum order")) return `🛒 ${detail}`;

        return typeof detail === 'string' && detail.trim() !== "" ? detail : "❌ Wrong coupon code. Please check and try again.";
    };

    const validateCoupon = async (couponData) => {
        if (!couponData.code || couponData.code.trim() === "") return;

        try {
            const cleanCode = couponData.code.trim().toUpperCase();
            setApplyingCoupon(cleanCode);

            // Send to validation endpoint
            const res = await API.post("/coupon/validate", {
                coupon_code: cleanCode,
                cart_total: subtotal,
                vendor_id: vendorId, // 👈 PASS VENDOR ID IN PAYLOAD
            });

            // 🚨 Catch "swallowed" errors where API interceptor prevents Axios from throwing
            if (
                res.status >= 400 ||
                res.data?.detail ||
                res.data?.error ||
                res.data?.success === false
            ) {
                const error = new Error(
                    res.data?.detail ||
                    res.data?.message ||
                    res.data?.error ||
                    "Invalid coupon"
                );

                error.response = res;
                throw error;
            }

            // Fallback safety check if network returned empty object
            if (!res.data || !res.data.coupon) {
                throw new Error("Invalid coupon");
            }

            // Use the backend's returned coupon details
            onCouponApplied(res.data.coupon, res.data.discount);

            setManualCode(""); // clear input
            setOpen(false); // Only close drawer on TRUE success
        } catch (err) {
            console.error("Coupon Error Caught:", err);
            setSnackbar({
                open: true,
                message: getCouponErrorMessage(err),
                severity: "error",
            });
        } finally {
            setApplyingCoupon(null);
        }
    };

    const removeCoupon = () => {
        onCouponApplied(null, 0);
    };

    return (
        <>
            {/* Main Trigger Card */}
            <Card
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 3,
                    borderRadius: 3,
                    border: selectedCoupon ? "1px solid #16A34A" : "1px solid #E5E7EB",
                    bgcolor: selectedCoupon ? "#F0FDF4" : "#FFFFFF",
                    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.04)",
                    cursor: selectedCoupon ? "default" : "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                        borderColor: selectedCoupon ? "#16A34A" : "#D1D5DB",
                    },
                }}
                onClick={() => {
                    if (!selectedCoupon) {
                        setOpen(true);
                    }
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: selectedCoupon ? "#DCFCE7" : "#FEF2F2",
                            }}
                        >
                            {selectedCoupon ? (
                                <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                            ) : (
                                <LocalOfferIcon sx={{ color: "#EF4444", fontSize: 18 }} />
                            )}
                        </Box>

                        <Box>
                            {selectedCoupon ? (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="700"
                                        sx={{ color: "#15803D", lineHeight: 1.2 }}
                                    >
                                        {selectedCoupon.code} Applied
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#16A34A", fontWeight: 600 }}
                                    >
                                        Saved ₹{couponDiscount}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: "#111827" }}>
                                        Apply Coupon
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                        Unlock offers & discounts
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Stack>

                    {selectedCoupon ? (
                        <Button
                            disableElevation
                            size="small"
                            sx={{
                                color: "#EF4444",
                                fontWeight: 700,
                                textTransform: "none",
                                minWidth: "auto",
                                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeCoupon();
                            }}
                        >
                            Remove
                        </Button>
                    ) : (
                        <IconButton size="small" sx={{ color: "#9CA3AF" }}>
                            <ChevronRightIcon />
                        </IconButton>
                    )}
                </Stack>
            </Card>

            {/* Bottom Drawer */}
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 20,
                        borderTopRightRadius: 20,
                        maxHeight: "85vh",
                        bgcolor: "#F9FAFB",
                        overflow: "visible",
                    },
                }}
            >
                {/* Floating Close Button Outside Drawer */}
                <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                        position: "absolute",
                        top: -56,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: "rgba(0, 0, 0, 0.4)",
                        color: "#FFF",
                        backdropFilter: "blur(4px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.6)",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ px: 2.5, py: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight="800" sx={{ color: "#111827" }}>
                        Offers & Benefits
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280", mb: 2, display: "block" }}>
                        Choose a coupon code to apply to your order
                    </Typography>

                    {/* Manual Input Box for Special Coupons */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Enter special coupon code"
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && manualCode.trim()) {
                                    validateCoupon({ code: manualCode });
                                }
                            }}
                            sx={{
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-root': { borderRadius: 2 }
                            }}
                        />
                        <Button
                            variant="contained"
                            disabled={!manualCode.trim() || applyingCoupon === manualCode.trim().toUpperCase()}
                            onClick={() => validateCoupon({ code: manualCode })}
                            sx={{
                                bgcolor: '#16A34A',
                                color: '#FFF',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 3,
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#15803D", boxShadow: "none" }
                            }}
                        >
                            {applyingCoupon === manualCode.trim().toUpperCase() ? <CircularProgress size={18} color="inherit" /> : "Apply"}
                        </Button>
                    </Box>

                    {/* Compact Coupons List */}
                    <Box sx={{ pb: 2, overflowY: "auto", maxHeight: "50vh" }}>

                        {coupons.length === 0 ? (

                            <Box
                                sx={{
                                    py: 4,
                                    px: 2,
                                    textAlign: "center",
                                }}
                            >
                                <LocalOfferIcon
                                    sx={{
                                        fontSize: 48,
                                        color: "#D1D5DB",
                                        mb: 1,
                                    }}
                                />

                                {totalCoupons === 0 ? (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#374151",
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            No Coupons Available
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                color: "#9CA3AF",
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            There are currently no offers available.
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#374151",
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            No Eligible Coupons
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                color: "#9CA3AF",
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            There are currently no offers available.
                                        </Typography>
                                    </>
                                )}
                            </Box>

                        ) : (

                            coupons.map((coupon) => {

                                const isApplied =
                                    selectedCoupon?.code === coupon.code;

                                const isApplying =
                                    applyingCoupon === coupon.code;

                                // Calculate potential discount strictly
                                let potentialDiscount = 0;
                                if (coupon.discount_type === "PERCENTAGE") {
                                    potentialDiscount = (subtotal * coupon.discount_value) / 100;
                                    if (coupon.max_discount) {
                                        potentialDiscount = Math.min(potentialDiscount, coupon.max_discount);
                                    }
                                } else {
                                    potentialDiscount = Math.min(coupon.discount_value, subtotal);
                                }

                                const roundedPotential = Math.round(potentialDiscount);

                                // EXACT SHORTFALL CALCULATION
                                const currentPostDiscount = subtotal - roundedPotential;
                                const meetsMinimum = currentPostDiscount >= coupon.minimum_order_amount;
                                const amountNeeded = Number((coupon.minimum_order_amount - currentPostDiscount).toFixed(2));

                                return (
                                    <Card
                                        key={coupon.id}
                                        elevation={0}
                                        sx={{
                                            mb: 1.5,
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: isApplied
                                                ? "#16A34A"
                                                : "#E5E7EB",
                                            bgcolor: isApplied
                                                ? "#F0FDF4"
                                                : "#FFFFFF",
                                            position: "relative",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Box p={2}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <Box>
                                                    <Typography
                                                        fontWeight={800}
                                                        fontSize="1rem"
                                                    >
                                                        {coupon.code}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {coupon.coupon_name || "Special Offer"}
                                                    </Typography>

                                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                                        Min Order ₹{coupon.minimum_order_amount}
                                                    </Typography>

                                                    {/* DYNAMIC SHORTFALL WARNING */}
                                                    {!meetsMinimum && (
                                                        <Typography variant="caption" sx={{ color: "#EF4444", fontWeight: 700, display: "block", mt: 0.5 }}>
                                                            Add ₹{amountNeeded} more to apply
                                                        </Typography>
                                                    )}

                                                    {coupon.discount_type === "PERCENTAGE" &&
                                                        coupon.max_discount && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block"
                                                            >
                                                                Discount upto ₹{coupon.max_discount}
                                                            </Typography>
                                                        )}

                                                    {coupon.one_time_per_user && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "#D97706",
                                                                fontWeight: 700,
                                                                display: "block",
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            One Time Use
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={isApplying || !meetsMinimum}
                                                    onClick={() => validateCoupon(coupon)}
                                                    sx={{
                                                        textTransform: "none",
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    {isApplying ? (
                                                        <CircularProgress
                                                            size={18}
                                                            color="inherit"
                                                        />
                                                    ) : (
                                                        "Apply"
                                                    )}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </Card>
                                );
                            })

                        )}

                    </Box>
                </Box>
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{
                    top: "75px !important",
                    left: "50% !important",
                    transform: "translateX(-50%) !important",
                    width: "92%",
                    maxWidth: "360px",
                    zIndex: 2000 // Forces the error to overlay on top of the drawer
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        display: "flex", alignItems: "center", width: "100%", py: 1.5, px: 2,
                        borderRadius: "16px",
                        bgcolor: "#E85D3F",
                        border: "1px solid #c9462d",
                        boxShadow: "0 4px 16px rgba(58, 44, 35, 0.08)",
                    }}
                >
                    <ErrorOutlineIcon sx={{ color: "#FFFDF8", fontSize: 20, mr: 1.5, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#FFFDF8", lineHeight: 1.35 }}>
                        {snackbar.message}
                    </Typography>
                </Paper>
            </Snackbar>

        </>
    );
}